import type { SiteJobDescriptionPublicDetailResponse } from '@flex-apis/recruiting';
import { JobDescriptionStatus, PageType } from '@flex-apis/recruiting';
import { fetchI18nResources, getTranslationsProps } from '@flex-packages/i18n';
import { serverSideTranslations } from '@flex-packages/i18n/next/serverSideTranslations';
import type { PublishedRecruitingSiteResponse } from '@flex-private-packages/recruiting-site-builder/base';
import {
  generatePublicJobDescriptionDetail,
  generateSiteDesign,
} from '@flex-private-packages/recruiting-site-builder/base';
import type { GetServerSideProps } from 'next';

import type { WithErrorProps } from '../../../src';
import {
  assertPublishedSite,
  ErrorPage,
  ErrorSubdomain,
  fetchJobDescriptionForPublic,
  fetchRecruitingSite,
  getExternalRedirectResult,
  getSubdomainFromServerSidePropsContext,
  getUrlQueryParamFromServerSidePropsContext,
  handleServerSideError,
  handleSystemSubdomain,
  JobDescriptionDetailPage,
  useSendAccessLog,
  withErrorPage,
} from '../../../src';
import { getServerAxios } from '../../../src/utils/axios';

interface Props {
  recruitingSiteResponse: PublishedRecruitingSiteResponse;
  siteJobDescriptionPublicDetailResponse: SiteJobDescriptionPublicDetailResponse;
}

function JobDescriptionDetail({
  recruitingSiteResponse,
  siteJobDescriptionPublicDetailResponse,
}: Props) {
  const siteDesign = generateSiteDesign(recruitingSiteResponse);
  const jobDescription = generatePublicJobDescriptionDetail(
    siteJobDescriptionPublicDetailResponse
  );

  useSendAccessLog({
    customerIdHash: siteDesign.customerIdHash,
    pageType: PageType.JobDescription,
  });

  return (
    <JobDescriptionDetailPage
      siteDesign={siteDesign}
      jobDescription={jobDescription}
    />
  );
}

const JobDescriptionDetailWithErrorPage = withErrorPage(JobDescriptionDetail);
export default JobDescriptionDetailWithErrorPage;

type ServerSideProps = WithErrorProps<Props>;
export const getServerSideProps: GetServerSideProps<
  ServerSideProps
> = async context => {
  try {
    const axiosInstance = getServerAxios({
      req: context.req,
      res: context.res,
    });
    const subdomain = getSubdomainFromServerSidePropsContext(context);
    const jobDescriptionIdHash = getUrlQueryParamFromServerSidePropsContext(
      context,
      'jobDescriptionIdHash'
    );

    await handleSystemSubdomain({ context, axiosInstance, subdomain });
    const recruitingSiteResponse = await fetchRecruitingSite(axiosInstance, {
      subdomain,
    });

    const externalRedirect = getExternalRedirectResult(recruitingSiteResponse);
    if (externalRedirect !== null) {
      return externalRedirect;
    }

    assertPublishedSite(recruitingSiteResponse);

    const { customerIdHash } = recruitingSiteResponse;
    const [
      siteJobDescriptionPublicDetailResponse,
      { props: translationProps },
    ] = await Promise.all([
      fetchJobDescriptionForPublic(axiosInstance, {
        customerIdHash,
        jobDescriptionIdHash,
      }),
      getTranslationsProps(
        serverSideTranslations,
        fetchI18nResources(require.context('../../../locales'))
      )(context),
    ]);

    if (
      siteJobDescriptionPublicDetailResponse.status ===
      JobDescriptionStatus.Private
    ) {
      throw new ErrorPage(
        '비공개된 공고입니다.',
        ErrorSubdomain.JobDescriptionClosedSubdomain
      );
    }

    return {
      props: {
        siteJobDescriptionPublicDetailResponse,
        recruitingSiteResponse,
        ...translationProps,
      },
    };
  } catch (error) {
    return handleServerSideError(context, error);
  }
};
